import React from 'react';
import { Phone, PhoneCall, Award, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { doctorInfo } from '../data/mock';

const Hero = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${doctorInfo.whatsapp}?text=${encodeURIComponent(doctorInfo.whatsappMessage)}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${doctorInfo.phone.replace(/\s/g, '')}`;
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-white via-[#7DAAAF]/15 to-white">
      {/* Decorative background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#7DAAAF]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#3D2418]/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #7DAAAF 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 w-full">
        <div className="text-center max-w-5xl mx-auto">
          {/* Badge */}
          <div className="inline-flex flex-wrap items-center justify-center bg-[#3D2418] border-2 border-[#7DAAAF] rounded-full px-4 sm:px-8 py-3 sm:py-4 mb-6 sm:mb-8 shadow-2xl hover:scale-105 transition-transform animate-in max-w-full">
            <Award className="w-4 h-4 sm:w-6 sm:h-6 text-[#7DAAAF] mr-2 sm:mr-3 flex-shrink-0" />
            <span className="text-xs sm:text-base font-bold text-[#7DAAAF] text-center leading-snug">
              {doctorInfo.name} | {doctorInfo.specialty} | {doctorInfo.collegiateNumber}
            </span>
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[#7DAAAF] ml-2 sm:ml-3 animate-pulse flex-shrink-0" />
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#3D2418] mb-6 sm:mb-8 leading-tight animate-in px-2">
            Tu médico a domicilio
            <span className="block text-[#7DAAAF] mt-2">en Valladolid</span>
          </h1>

          {/* Description */}
          <p className="text-base sm:text-xl md:text-2xl text-gray-700 mb-8 sm:mb-12 leading-relaxed max-w-3xl mx-auto animate-in px-4">
            {doctorInfo.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 animate-in px-4 sm:px-0">
            <Button 
              onClick={handleCall}
              data-testid="hero-call-btn"
              size="lg"
              className="bg-[#7DAAAF] hover:bg-[#6a959a] text-white text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-7 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl font-bold group w-full sm:w-auto"
            >
              <PhoneCall className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 group-hover:rotate-12 transition-transform flex-shrink-0" />
              <span className="truncate">Llamar: {doctorInfo.phone}</span>
            </Button>
            <Button 
              onClick={handleWhatsApp}
              data-testid="hero-whatsapp-btn"
              size="lg"
              className="bg-[#25D366] hover:bg-[#20bd5a] text-white text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-7 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl font-bold group w-full sm:w-auto"
            >
              <Phone className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 group-hover:scale-110 transition-transform flex-shrink-0" />
              WhatsApp
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 sm:gap-12 lg:gap-16 animate-in px-2">
            <div className="text-center group cursor-pointer min-w-[80px]">
              <div className="mb-2 sm:mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <p className="text-3xl sm:text-5xl md:text-6xl font-bold text-gradient">+25</p>
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium leading-tight">Años de<br/>experiencia</p>
            </div>
            <div className="text-center group cursor-pointer min-w-[80px]">
              <div className="mb-2 sm:mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <p className="text-3xl sm:text-5xl md:text-6xl font-bold text-gradient">+500</p>
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium leading-tight">Pacientes<br/>atendidos</p>
            </div>
            <div className="text-center group cursor-pointer min-w-[80px]">
              <div className="mb-2 sm:mb-3 transform group-hover:scale-110 transition-transform duration-300">
                <div className="flex items-center justify-center">
                  <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-[#7DAAAF]" />
                </div>
              </div>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium uppercase leading-tight px-2">Atención<br/>Presencial y<br/>Telefónica</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#7DAAAF] rounded-full flex justify-center">
          <div className="w-1 h-3 bg-[#7DAAAF] rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
